import { useEffect, useMemo, useState } from 'react';

import { Box } from '#/components/Box/Box';
import { EventDetailsList } from '#/components/DetailsList/Event/EventDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useAppConfig } from '#/config/AppConfigContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsExternalUrl } from '#/hooks/useIsExternalUrl';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarEventAttendees } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventAttendees';
import { ListingViewSidebarEventHost } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventHost';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewCommentSection } from '#/modules/listing/components/ListingView/sections/ListingViewCommentSection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { useListingApiFetchRsvpData } from '#/modules/listing/events/api/listingApiFetchRsvpData';
import type { ApiEvent } from '#/modules/listing/events/api/types/ApiEvent';
import { ViewEventActionButtons } from '#/modules/listing/events/components/ActionButtons/ViewEventActionButtons';
import { getEventApplySectionTitle } from '#/modules/listing/events/helpers/getEventApplySectionTitle';
import { RsvpAnswerContext } from '#/modules/listing/events/hooks/useRsvpAnswer';
import { EventShowAllRsvpsModalLoadable } from '#/modules/listing/events/modals/EventShowAllRsvpsModalLoadable';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { showModal } from '#/zustand-stores/modalStore';

import { canDisplayApplyDetails } from './canDisplayApplyDetails';

type Props = {
  event: ApiEvent;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
};

export function ViewEventPage({
  event,
  initiallySaved,
  orgInitiallySaved,
}: Props) {
  const { i18n, locale } = useI18n();
  const { isExternalUrl } = useIsExternalUrl();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: event.id,
  });

  const { user } = useUser();
  const [answer, setAnswer] = useState<string | null>(null);
  const [showRsvpsModal, setShowRsvpsModal] = useState(false);
  const isOrgOwner = isUserOrgOwner(user, event.org?.id);
  const rsvpAnswerContextValue = useMemo(
    () => ({ answer, setAnswer }),
    [answer],
  );

  const { appConfig } = useAppConfig();
  const openwebEnabled = Boolean(appConfig.openwebSpotId);
  const showComments = openwebEnabled && event.hasComments;

  const [rsvpDataResult, { reload, loading: rsvpDataLoading }] =
    useListingApiFetchRsvpData(event.id);

  useEffect(() => {
    // Refresh RSVP data when current user's own RSVP answer changes
    if (answer) void reload();
  }, [answer, reload]);

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts: false,
      listingType: event.type,
      listingId: event.id,
      listingName: event.name,
      orgId: event.org?.id,
      orgName: event.org?.name,
      orgType: event.org?.orgType,
    });
  }, [event]);

  const orgId = event.org?.id ?? null;
  const orgType = event.org?.orgType ?? null;
  const orgLogo = event.org?.logo ?? null;
  const orgName = event.org?.name ?? null;
  const orgUrl = event.org?.url[locale] ?? null;

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: event,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore: null,
  });

  return (
    <RsvpAnswerContext.Provider value={rsvpAnswerContextValue}>
      {isOrgOwner && event.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this event')}
          tools={
            <ListingViewAdminTools
              listingId={event.id}
              listingType="EVENT"
              orgId={event.org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === event.org?.id)?.canPostEvents,
              )}
              hasAts={false}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={event.address}
            listingName={event.name}
            listingType="EVENT"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={event.remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={event.firstPublished}
            listingType={event.type}
            showScrollToApplyButton={false}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            applied={false}
          />

          {event.hasRsvp && (
            <ListingViewSidebarEventAttendees
              loading={rsvpDataLoading}
              rsvps={rsvpDataResult?.rsvps}
              count={rsvpDataResult?.count}
              onClick={() => setShowRsvpsModal(true)}
            />
          )}

          {event.creator && (
            <ListingViewSidebarEventHost creator={event.creator} />
          )}

          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={event.address}
            hasRsvp={event.hasRsvp}
            isPostedAnonymously={false}
            listingId={event.id}
            listingName={event.name}
            listingType={event.type}
            listingFirstPublished={event.firstPublished}
            orgName={orgName}
            orgType={event.org?.orgType ?? null}
            orgUrl={orgUrl}
            remoteOptions={event.remoteOptions}
            isIdealistDay={event.isIdealistDay}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            actionButtons={
              <ViewEventActionButtons
                event={event}
                eventSaved={saved}
                onSaveEvent={save}
                onUnsaveEvent={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <EventDetailsList
              areasOfFocus={event.areasOfFocus}
              startDate={event.startDate}
              endDate={event.endDate}
              startsLocal={event.startsLocal}
              endsLocal={event.endsLocal}
              feeAmount={event.feeAmount}
              feeRequired={event.feeRequired}
              timezone={event.timezone}
              welcomeGroups={event.welcomeGroups}
              welcomeFamilies={event.welcomeFamilies}
              detailsWheelchairAccessible={event.detailsWheelchairAccessible}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={event.name}
            image={event.image}
            description={event.description}
            descriptionTruncated={event.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: [],
                  orgId: event.org?.id,
                  listingName: event.name,
                  listingType: 'EVENT',
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this event')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: event.type,
                locale: event.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={event.type}
            address={event.address}
            directions={event.directions}
            remoteOptions={event.remoteOptions}
          />

          {canDisplayApplyDetails(event) ? (
            <ListingViewApplySection
              title={getEventApplySectionTitle({ i18n })}
            >
              <ListingViewApplyExternal
                applyEmail={event.applyEmail}
                applyText={event.applyText}
                applyTextTruncated={event.applyTextTruncated}
                applyUrl={event.applyUrl}
                applied={false}
                onMarkAsApplied={() => Promise.resolve()}
                functions={[]}
                listingName={event.name}
                listingType="EVENT"
                listingId={event.id}
                orgId={orgId}
                orgType={orgType}
                synonym={null}
                isPreview={false}
              />
            </ListingViewApplySection>
          ) : null}

          {event.hasComments && (
            <div data-qa-id="openweb-container-outer">
              {showComments && <ListingViewCommentSection listing={event} />}
            </div>
          )}

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={event.url[locale]}
              type={event.type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}

      {showRsvpsModal && rsvpDataResult && (
        <EventShowAllRsvpsModalLoadable
          eventId={event.id}
          count={rsvpDataResult.count}
          initialRsvps={rsvpDataResult.rsvps}
          onClose={() => setShowRsvpsModal(false)}
        />
      )}
    </RsvpAnswerContext.Provider>
  );
}
